import { env } from "@/core/config/env";
import { authSession } from "./authSession";

const AUTH_PATH_PREFIXES = [
  "/auth",
  "/users/settings",
  "/roles-permissions",
  "/locations",
  "/designations",
  "/agreements",
  "/languages",
  "/onboarding",
] as const;

export const stripTrailingSlash = (url: string): string =>
  url.replace(/\/+$/, "");

const pathWithoutQuery = (url: string): string => {
  const [path] = url.split("?");
  if (!path) return "";
  return path.startsWith("http") ? new URL(path).pathname : path;
};

export const isAuthApiPath = (url: string): boolean => {
  const path = pathWithoutQuery(url);
  return AUTH_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
};

export const getAuthApiBaseUrl = (): string =>
  stripTrailingSlash(env.apiBaseUrl);

/** Join a register `download_path` (`/api/v1/...` or relative) onto the auth host. */
export const resolveAuthResourceUrl = (pathOrUrl: string): string => {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const authBase = getAuthApiBaseUrl();
  if (pathOrUrl.startsWith("/api/v1/")) {
    const origin = authBase.replace(/\/api\/v1$/i, "");
    return `${origin}${pathOrUrl}`;
  }
  const relative = pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`;
  return `${authBase}${relative}`;
};

/** Regional host from login, e.g. `api.dev.in.maya-mind.com` → `https://…/api/v1`. */
export const normalizeClinicalApiBaseUrl = (
  raw: string | null | undefined,
): string | null => {
  if (!raw?.trim()) return null;
  let value = raw.trim().replace(/\/+$/, "");
  if (!/^https?:\/\//i.test(value)) {
    value = `https://${value.replace(/^\/+/, "")}`;
  }

  try {
    const parsed = new URL(value);
    const path = parsed.pathname.replace(/\/+$/, "");
    if (!path || path === "/") {
      return `${parsed.origin}/api/v1`;
    }
    if (/\/api\/v1$/i.test(path) || /^\/api\/v1\//i.test(path)) {
      return `${parsed.origin}/api/v1`;
    }
    return `${parsed.origin}${path}`.replace(/\/+$/, "");
  } catch {
    return null;
  }
};

/** Regional clinical API from the auth login /me response. Null until login. */
export const getClinicalApiBaseUrl = (): string | null =>
  normalizeClinicalApiBaseUrl(authSession.getClinicalApiBaseUrl());

export const resolveApiBaseUrl = (url: string): string | null => {
  if (isAuthApiPath(url)) return getAuthApiBaseUrl();
  return getClinicalApiBaseUrl();
};
